'use client';

import type { TDynamicData } from '../../types/dynamic-data';

import { useState } from 'react';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchDynamicSectionsPaged } from '@eliadmin/core/src/api/dynamics/dynamics-api';
import { useInfiniteQuery } from '@tanstack/react-query';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { TripCard } from './trip-card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { Button } from '../ui/button';
import type { DynamicSectionCategoryProps } from '../../pages/tip-for-trip/page';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export const TripsList = ({
  locale,
  categories,
}: {
  locale: string;
  categories: DynamicSectionCategoryProps[];
}) => {
  const { t } = useTranslations();
  const [activeTab, setActiveTab] = useState(categories[0].name as string);

  const tripsQuery = useInfiniteQuery({
    queryKey: [CacheKey.SLUG_DETAIL, activeTab],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchDynamicSectionsPaged<TDynamicData>({
        locale,
        sectionIds: [2],
        query: { valueFilters: [{ fieldId: 13, values: activeTab }], results: 9 },
        page: pageParam as number,
      });
    },
    getNextPageParam: (lastPage: any, pages) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const trips = tripsQuery.data?.pages.flatMap((page: any) => page.data) ?? [];

  return (
    <Tabs
      onValueChange={value => setActiveTab(value as string)}
      defaultValue={activeTab}
      className={cn('custom-section', 'ea-trips-list__tabs', 'mt-24 sm:mt-32')}
    >
      <TabsList
        className={cn(
          'ea-trips-list__tabs-list',
          'hide-scroll-bar',
          'mx-auto flex h-fit w-full items-center justify-start gap-0 overflow-x-scroll rounded-none bg-secondary p-1 md:w-fit md:justify-center',
        )}
      >
        {categories.map(category => (
          <TabsTrigger
            value={category.name}
            key={category.name}
            className={cn(
              'ea-trips-list__tabs-trigger',
              'rounded-none bg-transparent px-8 py-[14px] text-base text-primary shadow-none',
            )}
          >
            {category.name}
          </TabsTrigger>
        ))}
      </TabsList>
      <TabsContent
        value={activeTab}
        className={cn(
          'ea-trips-list__tabs-content',
          'custom-container',
          'm-0 grid w-full grid-cols-1 gap-6 sm:grid-cols-2 md:gap-8 lg:grid-cols-3 xl:gap-12',
        )}
      >
        {trips && trips.length > 0 ? (
          trips.map(trip => <TripCard key={trip.id} trip={trip} />)
        ) : (
          <p className="col-span-1 w-full text-center sm:col-span-2 lg:col-span-3">
            Žiadne tipy sa v kategórii <span className="font-bold text-primary">{activeTab}</span>{' '}
            nenašli.
          </p>
        )}
        <div
          className={cn('col-span-1 flex items-center justify-center sm:col-span-2 lg:col-span-3', {
            hidden: !tripsQuery.hasNextPage,
          })}
        >
          <Button
            onClick={() => tripsQuery.fetchNextPage()}
            isLoading={tripsQuery.isFetchingNextPage}
            variant={'default-alt'}
          >
            {t('text338')}
          </Button>
        </div>
      </TabsContent>
    </Tabs>
  );
};
